/**
 * v4.11.0 — Fleet Table.
 *
 * Server-paginated. Calm dark surface. Hospitality-readable rows. Click
 * a row → opens the in-page detail drawer. Mobile collapses extra
 * columns into a stacked card.
 */
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight, MoreVertical, ShieldCheck, Shield, LinkIcon } from "lucide-react";
import ScheduleSnapshot from "./ScheduleSnapshot";
import SelectionCheckbox from "./bulk/SelectionCheckbox";
import PlatformIcon from "./PlatformIcon";

const Pill = ({ children, color, testId }) => (
    <span
        data-testid={testId}
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium"
        style={{
            background: `${color}1A`,
            color,
            border: `1px solid ${color}33`,
        }}
    >
        {children}
    </span>
);

const ModeBadge = ({ mode, submode }) => {
    const { t } = useTranslation();
    if (mode === "review_shield") {
        return (
            <Pill color="#10b981" testId="mode-review-shield">
                <ShieldCheck size={11} strokeWidth={1.9} />
                {t(`card_fleet.mode.review_shield`)}
                {submode === "strict" ? (
                    <span className="text-[9px] opacity-75">· {t("card_fleet.mode.strict")}</span>
                ) : null}
            </Pill>
        );
    }
    if (mode === "direct") {
        return (
            <Pill color="#60a5fa" testId="mode-direct">
                <LinkIcon size={11} strokeWidth={1.9} />
                {t(`card_fleet.mode.direct`)}
            </Pill>
        );
    }
    return (
        <Pill color="#a78bfa" testId="mode-standard">
            <Shield size={11} strokeWidth={1.9} />
            {t(`card_fleet.mode.standard`)}
            {submode === "campaign" ? (
                <span className="text-[9px] opacity-75">· {t("card_fleet.mode.campaign")}</span>
            ) : null}
        </Pill>
    );
};

const StatusDot = ({ status }) => {
    const { t } = useTranslation();
    const map = {
        active: "#10b981",
        inactive: "#94a3b8",
        paused: "#f59e0b",
        unassigned: "#64748b",
    };
    return (
        <span className="inline-flex items-center gap-1.5 text-xs text-slate-200">
            <span
                className="inline-block w-2 h-2 rounded-full"
                style={{ background: map[status] || "#94a3b8" }}
            />
            {t(`card_fleet.status.${status}`, { defaultValue: status })}
        </span>
    );
};

export default function FleetTable({
    rows, total, page, pages, pageSize,
    onPageChange, onPageSizeChange, onRowClick, loading,
    selectedIds = new Set(), onToggleSelection, onToggleAllOnPage,
}) {
    const { t } = useTranslation();
    const anySelected = selectedIds.size > 0;
    const allOnPageSelected = rows.length > 0 && rows.every((r) => selectedIds.has(r.id));

    return (
        <div
            className="rounded-xl border overflow-hidden"
            style={{
                background: "rgba(15, 23, 42, 0.55)",
                borderColor: "rgba(148, 163, 184, 0.12)",
            }}
            data-testid="fleet-table"
            data-insight-id="fleet.table"
            data-insight-label="Fleet table"
        >
            {/* Header */}
            <div className="hidden lg:grid text-[11px] uppercase tracking-wider text-slate-400 px-4 py-3 border-b items-center"
                 style={{
                     borderColor: "rgba(148, 163, 184, 0.10)",
                     gridTemplateColumns: "32px 1.2fr 1.4fr 1.6fr 1fr 1.2fr 0.7fr 0.7fr 60px",
                 }}>
                <div>
                    <SelectionCheckbox
                        checked={allOnPageSelected}
                        anySelected={anySelected}
                        onToggle={() => onToggleAllOnPage?.(!allOnPageSelected)}
                        testId="fleet-select-all"
                    />
                </div>
                <div>{t("card_fleet.col.card_location")}</div>
                <div>{t("card_fleet.col.current_destination")}</div>
                <div>{t("card_fleet.col.schedule_snapshot")}</div>
                <div>{t("card_fleet.col.mode")}</div>
                <div>{t("card_fleet.col.registered_to")}</div>
                <div className="text-right">{t("card_fleet.col.taps_today")}</div>
                <div>{t("card_fleet.col.status")}</div>
                <div className="text-right">{t("card_fleet.col.actions")}</div>
            </div>

            {/* Rows */}
            <div>
                {loading && rows.length === 0 ? (
                    <div className="p-8 text-center text-sm text-slate-400" data-testid="fleet-table-loading">
                        {t("common.loading")}
                    </div>
                ) : rows.length === 0 ? (
                    <div className="p-10 text-center text-sm text-slate-400" data-testid="fleet-table-empty">
                        {t("card_fleet.empty.title")}
                    </div>
                ) : (
                    rows.map((c, idx) => {
                        const isSelected = selectedIds.has(c.id);
                        // Tag only the first row as a teach target — every row
                        // is visually identical, so one example is enough for
                        // an operator to learn the row → drawer workflow.
                        const insightId = idx === 0 ? "fleet.row.open-drawer" : undefined;
                        return (
                        <div
                            key={c.id}
                            role="button"
                            tabIndex={0}
                            onClick={() => onRowClick(c)}
                            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onRowClick(c); } }}
                            data-testid={`fleet-row-${c.id}`}
                            data-selected={isSelected ? "true" : "false"}
                            data-insight-id={insightId}
                            data-insight-label={insightId ? "Open card detail" : undefined}
                            className={`group w-full text-left px-4 py-3 border-b last:border-b-0 hover:bg-white/[0.025] transition-colors cursor-pointer ${
                                isSelected ? "bg-sky-400/[0.06]" : ""
                            }`}
                            style={{ borderColor: "rgba(148, 163, 184, 0.06)" }}
                        >
                            {/* Desktop grid */}
                            <div className="hidden lg:grid items-center gap-3 text-sm"
                                 style={{ gridTemplateColumns: "32px 1.2fr 1.4fr 1.6fr 1fr 1.2fr 0.7fr 0.7fr 60px" }}>
                                <div>
                                    <SelectionCheckbox
                                        checked={isSelected}
                                        anySelected={anySelected}
                                        onToggle={(v) => onToggleSelection?.(c.id, v)}
                                        testId={`fleet-select-${c.id}`}
                                        insightId={idx === 0 ? "fleet.bulk.row-select" : undefined}
                                    />
                                </div>
                                <div className="min-w-0">
                                    <div className="font-medium text-slate-100 truncate">
                                        #{c.card_id || c.name || "—"}
                                    </div>
                                    <div className="text-xs text-slate-400 truncate">
                                        {c.location_name || t("card_fleet.unassigned_location")}
                                    </div>
                                </div>
                                <div className="min-w-0">
                                    <div className="text-slate-200 truncate flex items-center gap-1.5">
                                        <PlatformIcon platform={c.current_destination} size={14} brand />
                                        <span className="truncate">
                                            {t(`card_fleet.platforms.${c.current_destination}`, { defaultValue: c.current_destination || "—" })}
                                        </span>
                                    </div>
                                    <div className="text-xs text-slate-500 truncate">
                                        {c.schedule.enabled
                                            ? t("card_fleet.via_schedule")
                                            : t("card_fleet.direct_link")}
                                    </div>
                                </div>
                                <div className="min-w-0" data-insight-id="fleet.cell.schedule" data-insight-label="Schedule snapshot">
                                    <ScheduleSnapshot schedule={c.schedule} fallbackPlatform={c.platform} />
                                </div>
                                <div><ModeBadge mode={c.mode} submode={c.submode} /></div>
                                <div className="min-w-0 truncate">
                                    {c.employee_name ? (
                                        <span className="text-slate-200">
                                            {c.employee_name}
                                            {c.employee_code ? (
                                                <span className="text-slate-500 ml-1 text-xs">· {c.employee_code}</span>
                                            ) : null}
                                        </span>
                                    ) : (
                                        <span className="text-slate-500 text-xs italic">
                                            {t("card_fleet.unassigned_employee")}
                                        </span>
                                    )}
                                </div>
                                <div className="text-right font-mono text-slate-100">
                                    {(c.taps_today || 0).toLocaleString()}
                                </div>
                                <div><StatusDot status={c.status} /></div>
                                <div className="text-right">
                                    <span className="inline-block p-1.5 rounded-md hover:bg-white/5 text-slate-400">
                                        <MoreVertical size={14} strokeWidth={1.75} />
                                    </span>
                                </div>
                            </div>

                            {/* Mobile / tablet stacked card */}
                            <div className="lg:hidden">
                                <div className="flex items-start justify-between gap-2">
                                    <div className="flex items-center gap-2 min-w-0">
                                        <SelectionCheckbox
                                            checked={isSelected}
                                            anySelected={anySelected}
                                            onToggle={(v) => onToggleSelection?.(c.id, v)}
                                            testId={`fleet-select-${c.id}-m`}
                                        />
                                        <div className="min-w-0">
                                            <div className="font-medium text-slate-100 truncate">
                                                #{c.card_id || c.name || "—"}
                                            </div>
                                            <div className="text-xs text-slate-400 truncate">
                                                {c.location_name || t("card_fleet.unassigned_location")}
                                            </div>
                                        </div>
                                    </div>
                                    <ModeBadge mode={c.mode} submode={c.submode} />
                                </div>
                                <div className="mt-2 flex items-center justify-between gap-2 text-xs">
                                    <div className="truncate text-slate-200 flex items-center gap-1">
                                        <PlatformIcon platform={c.current_destination} size={12} brand />
                                        <span className="truncate">
                                            {t(`card_fleet.platforms.${c.current_destination}`, { defaultValue: c.current_destination || "—" })}
                                        </span>
                                    </div>
                                    <StatusDot status={c.status} />
                                </div>
                                <div className="mt-2">
                                    <ScheduleSnapshot schedule={c.schedule} fallbackPlatform={c.platform} />
                                </div>
                                <div className="mt-2 flex items-center justify-between text-xs text-slate-400">
                                    <span className="truncate">{c.employee_name || t("card_fleet.unassigned_employee")}</span>
                                    <span className="font-mono text-slate-200">
                                        {(c.taps_today || 0).toLocaleString()} {t("card_fleet.taps")}
                                    </span>
                                </div>
                            </div>
                        </div>
                        );
                    })
                )}
            </div>

            {/* Footer pagination */}
            <div className="flex items-center justify-between px-4 py-3 text-xs text-slate-400 border-t flex-wrap gap-2"
                 style={{ borderColor: "rgba(148, 163, 184, 0.10)" }}>
                <div data-testid="fleet-table-pagination-summary">
                    {t("card_fleet.pagination.showing", {
                        from: total === 0 ? 0 : (page - 1) * pageSize + 1,
                        to: Math.min(total, page * pageSize),
                        total: total.toLocaleString(),
                    })}
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={() => onPageChange(Math.max(1, page - 1))}
                        disabled={page <= 1}
                        data-testid="fleet-page-prev"
                        className="px-2 py-1 rounded hover:bg-white/5 disabled:opacity-30"
                    >
                        <ChevronLeft size={14} />
                    </button>
                    <span className="px-2 text-slate-200" data-testid="fleet-page-current">
                        {page} / {pages || 1}
                    </span>
                    <button
                        type="button"
                        onClick={() => onPageChange(Math.min(pages || 1, page + 1))}
                        disabled={page >= pages}
                        data-testid="fleet-page-next"
                        className="px-2 py-1 rounded hover:bg-white/5 disabled:opacity-30"
                    >
                        <ChevronRight size={14} />
                    </button>
                    <select
                        value={pageSize}
                        onChange={(e) => onPageSizeChange(parseInt(e.target.value, 10))}
                        data-testid="fleet-page-size"
                        className="ml-2 bg-transparent border rounded px-1.5 py-1 text-xs"
                        style={{ borderColor: "rgba(148, 163, 184, 0.2)" }}
                    >
                        {[25, 50, 100].map((n) => (
                            <option key={n} value={n}>{t("card_fleet.pagination.per_page", { n })}</option>
                        ))}
                    </select>
                </div>
            </div>
        </div>
    );
}
